import React, { useState, useEffect } from 'react';
import { Row, Col, Button, message } from 'antd';
import HeadingChip from 'Molecules/HeadingChip';
import ListCard from 'Molecules/ListCard';
import ActionButton from 'Molecules/ActionButton';
import Search from './Components/Search';
import { getAvailabilityList } from '../ducks/actions';
import { removeAvailability, bulkRemoveAvailabilities } from '../ducks/services';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AllRoles from '../../../../routing/config/AllRoles';
import { allowed } from '../../../../routing/config/utils';
import { getListQueryItems } from 'Modules/Application/ducks/actions';
import { useTranslate } from 'Translate';

export default (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchValue, setSearchVal] = useState(null);
  const availabilities = useSelector((state) => state.product.availabilities);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const allowedEdit = allowed([AllRoles.PRODUCT.AVAILABILITY], 'write');
  const allowedDelete = allowed([AllRoles.PRODUCT.AVAILABILITY], 'delete');
  const globalListQuery = useSelector((state) => state.global.listQueryItems);
  const i18n = useTranslate();
  const { t } = i18n;

  useEffect(() => {
    if(!globalListQuery.availableProductListQuery) {
      dispatch(getAvailabilityList(1, limit, '', '', null, props.setLoading));
      dispatch(getListQueryItems('availableProductListQuery', 1, limit, '', '', null, props.setLoading));
    } else {
      const {page, limit, sortOrder, sortColKey, searchValue} = globalListQuery.availableProductListQuery
      dispatch(getAvailabilityList(page, limit, sortOrder, sortColKey, searchValue, props.setLoading));
      setPage(page);
      setLimit(limit);
      setSearchVal(searchValue);
    }
  }, []);

  const reloadApi = () => {
    dispatch(getAvailabilityList(page, limit, '', '', null, props.setLoading));
    dispatch(getListQueryItems('availableProductListQuery', page, limit, '', '', null, props.setLoading))
  };

  const onRemove = (recordId) => {
    props.setLoading(true);
    let payload = {
      work_schedule_id: recordId,
      status_event: 'remove'
    }
    removeAvailability(payload)
      .then((response) => {
        props.setLoading(false);
        if (response.data.code == 200) {
          message.success(t("Product.product_avblty_delete_success"));
          reloadApi();
        } else {
          message.error(response.data.message);
        }
      })
      .catch((error) => {
        props.setLoading(false);
        message.error(error?.message ?? t("General.something_went_wrong"));
      });
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onBulkRemove = async () => {
    props.setLoading(true);

    const payload = {
      work_schedules: JSON.stringify(selectedRowKeys)
    }

    await bulkRemoveAvailabilities(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        rowSelection.onChange([]);
        message.success(t("Product.cats_remove_success"));
        setTimeout(() => dispatch(getCategories(1, limit, '', '', props.setLoading)), 500);
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const ListCol = [
    {
      title: t("Product.template_name"),
      dataIndex: 'name',
      key: 'template_name',
      className: 'enable-click',
      sorter: true,
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      render: (text, record) =>
        <ActionButton
          title="product_availability"
          btnAction1={() => navigate(`/product/availability/${record.id}`)}
          recordId={record.id}
          onRemove={allowedDelete && onRemove}
          setLoading={props.setLoading}
          loading={props.loading}
        />
    }
  ];

  const btnList = [
    {
      title: t("Product.confirm_remove_select_record"),
      text: t("General.bulk_remove"),
      classes: 'red-btn text-white attendance-success',
      permit: true,
      action: () => onBulkRemove(),
    },
    {
      text: `+ ${t("Product.new_product_avblty")}`,
      action: () => {
        navigate('add')
      },
    },
  ];

  const onSearch = (value) => {
    if (value) {
      let searchVal = {
        name: value?.template_name ? value?.template_name : '',
      };
      setSearchVal(searchVal);
      setPage(1);
      dispatch(getAvailabilityList(1, 10, '', '', searchVal, props.setLoading));
      dispatch(getListQueryItems('availableProductListQuery', 1, 10, '', '', searchVal, props.setLoading))
    }
  };

  const onTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
    if (sorter.order) {
      dispatch(
        getAvailabilityList(pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchValue, props.setLoading),
      );
      dispatch(getListQueryItems('availableProductListQuery', pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchValue, props.setLoading))
    } else {
      dispatch(getAvailabilityList(pagination.current, pagination.pageSize, '', '', searchValue, props.setLoading));
      dispatch(getListQueryItems('availableProductListQuery', pagination.current, pagination.pageSize, '', '', searchValue, props.setLoading))
    }
  };

  const onClickRow = (record) => {
    return {
      onClick: (e) => {
        if (e.target.closest(".enable-click"))
        navigate(`/product/availability/${record.id}`);
      },
    };
  };

  return (
    <>
      <Row gutter={[20, 30]}>
        <Col span={24}>
          <HeadingChip title={t("Product.product_avblty")} btnList={allowedEdit ? btnList : null} btnHidden={selectedRowKeys.length <= 0} />
        </Col>
        <Col span={24} className="clickRow">
          <ListCard
            onRow={onClickRow}
            Search={Search}
            onSearch={onSearch}
            searchVal={globalListQuery.availableProductListQuery?.searchValue}
            ListCol={ListCol}
            ListData={availabilities?.list?.map(x => ({...x, key: x.id}))}
            rowSelection={allowedEdit && rowSelection}
            pagination={{
              total: availabilities?.total_count,
              current: page,
              pageSize: limit,
            }}
            onChange={onTableChange}
          />
        </Col>
      </Row>
    </>
  );
};
